import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {QuizData} from "types/Quiz";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import QuizForm from "pages/Quiz/Form";
import {useOperator} from "../../../context/OperatorContext";

// Helpers ----

function removeKeyItemProperty(values: QuizData): QuizData {
    const clonedValues: QuizData = structuredClone(values);

    for (const question of clonedValues.questions) {
        for (const choice of question.choices) {
            if ('key' in choice) {
                delete choice.key
            }
        }
    }

    return clonedValues;
}

// Component ----

function MutateQuiz() {
    const {id} = useParams();
    const {termFor} = useOperator();
    const {data: quiz, isLoading} = useQuery(
        ['quizzes', {id}],
        () => ngage.quizzes.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: QuizData) => ngage.quizzes.store(data))
    const update = useMutation((data: QuizData) => ngage.quizzes.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: QuizData, {setSubmitting}: FormikHelpers<QuizData>) {
        const newValues = removeKeyItemProperty(values);

        id? await handleUpdate(newValues) : await handleStore(newValues)
        await invalidate('quizzes.all')

        setSubmitting(false)
        navigate('/quizzes')
    }

    // Handle store mutation
    async function handleStore(values: QuizData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Quiz')} Created`, `${values.title} has been created`)
        } catch (error) {
            notify("error", `${termFor('Quiz')} not created`, `${values.title} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: QuizData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Quiz')} updated`, `${values.title} has been updated`)
            await invalidate(['quizzes', {id}])
        } catch (error) {
            notify("error", `${termFor('Quiz')} not updated`, `${values.title} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'App Content': '/', 'Interactive': '/', [termFor('Quizzes')]: '/quizzes', [`New ${termFor('Quiz')}`]: '/quizzes/new'}
    const editBreadcrumbs = {'App Content': '/', 'Interactive': '/', [termFor('Quizzes')]: '/quizzes', [`Edit ${termFor('Quiz')}`]: `/quizzes/${id}/edit`}

    return (
        <Page title={id ? "Edit Quiz" : `New ${termFor('Quiz')}`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <QuizForm quiz={quiz} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateQuiz;